import React, { useEffect } from 'react';

const JoinOurTeams = () => {
  useEffect(() => {
    // --- Intersection Observer for Entrance Animation ---
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, index) => {
          if (entry.isIntersecting) {
            // Use a timeout to stagger the animation start
            setTimeout(() => {
              entry.target.dataset.isVisible = 'true';
            }, index * 150);
            observer.unobserve(entry.target); // Animate only once
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = document.querySelectorAll('[data-component="card"]');
    cards.forEach((card) => observer.observe(card));
    
    // --- Stylesheet Injection ---
    const styleSheet = document.createElement('style');
    styleSheet.textContent = `
      /* --- Keyframe Animations --- */
      @keyframes slideInScale {
        from { opacity: 0; transform: translateY(50px) scale(0.9); }
        to { opacity: 1; transform: translateY(0) scale(1); }
      }
      @keyframes meshRotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      @keyframes orbFloat {
        0%, 100% { transform: translateY(0px); opacity: 0.6; }
        50% { transform: translateY(-20px) scale(1.1); opacity: 1; }
      }

      /* --- Animation Trigger --- */
      [data-component="card"] {
        opacity: 0;
        transform: translateY(50px) scale(0.9);
      }
      [data-component="card"][data-is-visible="true"] {
        animation: slideInScale 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
      }
      
      /* --- Hover Effects --- */
      [data-component="card"]:hover {
        transform: translateY(-8px) scale(1.02);
        border-color: rgba(255, 255, 255, 0.15);
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
        background: linear-gradient(135deg, color-mix(in srgb, var(--team-color) 15%, transparent), color-mix(in srgb, var(--team-color) 25%, transparent));
      }
      [data-component="card"]:hover [data-component="card-pattern"] {
        opacity: 0.1;
      }
      [data-component="card"]:hover [data-component="icon-container"] {
        box-shadow: 0 0 30px color-mix(in srgb, var(--team-color) 40%, transparent);
      }
      [data-component="card"]:hover [data-component="action-button"] {
        background: linear-gradient(135deg, var(--team-color), color-mix(in srgb, var(--team-color) 85%, #000));
        color: white;
      }
      [data-component="card"]:hover [data-component="button-arrow"] {
        transform: translateX(5px);
      }

      /* --- Responsive Design --- */
      @media (max-width: 900px) {
        [data-component="grid"] {
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr)) !important;
        }
      }
      @media (max-width: 480px) {
        [data-component="container"] {
          padding: 80px 10px !important;
        }
        [data-component="grid"] {
          grid-template-columns: 1fr !important;
          padding: 0 !important;
        }
        [data-component="card"] {
          width: 95% !important;
          margin: 0 auto !important;
        }
        [data-component="card-content"] {
          padding: 1.5rem !important;
        }
      }
    `;
    document.head.appendChild(styleSheet);

    return () => {
      observer.disconnect();
      document.head.removeChild(styleSheet);
    };
  }, []);

  const teams = [
    { icon: '🔬', title: 'Research & Development', openings: 8, description: 'Pioneering breakthrough AI research and developing revolutionary educational technologies', color: '#6366f1', bgPattern: 'radial-gradient(circle at 20% 80%, #6366f1 0%, transparent 50%), radial-gradient(circle at 80% 20%, #8b5cf6 0%, transparent 50%)', tags: ['AI Research', 'Innovation', 'Future Tech'] },
    { icon: '⚡', title: 'Engineering', openings: 12, description: 'Building lightning-fast, scalable tools that empower millions of learners worldwide', color: '#8b5cf6', bgPattern: 'radial-gradient(circle at 20% 80%, #8b5cf6 0%, transparent 50%), radial-gradient(circle at 80% 20%, #ec4899 0%, transparent 50%)', tags: ['Tools', 'develop', 'Performance'] },
    { icon: '🎯', title: 'Education & Curriculum', openings: 6, description: 'Crafting & providing immersive learning experiences that transform how people understand AI', color: '#ec4899', bgPattern: 'radial-gradient(circle at 20% 80%, #ec4899 0%, transparent 50%), radial-gradient(circle at 80% 20%, #f472b6 0%, transparent 50%)', tags: ['Pedagogy', 'teaching', 'Content'] },
    { icon: '📈', title: 'Data Science & Analytics', openings: 4, description: 'Extracting powerful insights from learning data to revolutionize educational outcomes', color: '#10b981', bgPattern: 'radial-gradient(circle at 20% 80%, #10b981 0%, transparent 50%), radial-gradient(circle at 80% 20%, #34d399 0%, transparent 50%)', tags: ['Machine Learning', 'Analytics', 'Insights'] },
    { icon: '🌍', title: 'Community & Partnerships', openings: 3, description: 'Building bridges across continents to create a unified global learning ecosystem', color: '#f59e0b', bgPattern: 'radial-gradient(circle at 20% 80%, #f59e0b 0%, transparent 50%), radial-gradient(circle at 80% 20%, #fbbf24 0%, transparent 50%)', tags: ['Marketing', 'Partnerships', 'Community'] },
    { icon: '🛡️', title: 'Infrastructure & DevOps', openings: 5, description: 'Architecting bulletproof, secure systems that power global-scale AI education', color: '#06b6d4', bgPattern: 'radial-gradient(circle at 20% 80%, #06b6d4 0%, transparent 50%), radial-gradient(circle at 80% 20%, #0891b2 0%, transparent 50%)', tags: ['Testing', 'Security', 'DevOps'] }
  ];

  return (
    <div style={styles.container} data-component="container">
      <div style={styles.meshBackground}><div style={styles.mesh1}></div><div style={styles.mesh2}></div><div style={styles.mesh3}></div></div>
      <div style={styles.floatingOrbs}><div style={styles.orb1}></div><div style={styles.orb2}></div><div style={styles.orb3}></div><div style={styles.orb4}></div></div>

      <div style={styles.content}>
        <div style={styles.header}>
          <div style={styles.titleContainer}>
            <h2 style={styles.title}>Join Our Teams</h2>
            <div style={styles.titleAccent}></div>
          </div>
          <p style={styles.subtitle}>Where innovation meets passion. Choose your path to shape the future of AI education.</p>
        </div>
        
        <div style={styles.grid} data-component="grid">
          {teams.map((team, index) => (
            <div 
              key={index} 
              data-component="card"
              style={{ ...styles.card, '--team-color': team.color }}
            >
              <div data-component="card-pattern" style={{...styles.cardPattern, background: team.bgPattern, opacity: 0}}></div>
              <div style={styles.cardContent} data-component="card-content">
                <div style={styles.iconSection}>
                  <div data-component="icon-container" style={{ ...styles.iconContainer, background: `linear-gradient(135deg, ${team.color}20, ${team.color}40)`, boxShadow: `0 0 20px ${team.color}20` }}>
                    <span style={styles.icon}>{team.icon}</span>
                  </div>
                </div>
                <div style={styles.cardBody}>
                  <h3 style={styles.cardTitle}>{team.title}</h3>
                  <p style={styles.cardDescription}>{team.description}</p>
                  <div style={styles.tagsContainer}>
                    {team.tags.map((tag, tagIndex) => (
                      <span key={tagIndex} style={{ ...styles.tag, background: `${team.color}20`, color: team.color, borderColor: `${team.color}40` }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Base styles object (some properties are now controlled by the stylesheet)
const styles = {
  container: { position: 'relative', background: 'radial-gradient(ellipse at top, #0f0f1f 0%, #000000 70%)', padding: '120px 20px', minHeight: '100vh', overflow: 'hidden', fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif" },
  meshBackground: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0.4 },
  mesh1: { position: 'absolute', top: '10%', left: '10%', width: '600px', height: '600px', background: 'conic-gradient(from 0deg, #6366f120, transparent, #8b5cf620)', borderRadius: '50%', filter: 'blur(40px)', animation: 'meshRotate 20s linear infinite' },
  mesh2: { position: 'absolute', bottom: '10%', right: '10%', width: '400px', height: '400px', background: 'conic-gradient(from 90deg, #ec489920, transparent, #f472b620)', borderRadius: '50%', filter: 'blur(30px)', animation: 'meshRotate 15s linear infinite reverse' },
  mesh3: { position: 'absolute', top: '50%', left: '50%', width: '300px', height: '300px', background: 'conic-gradient(from 180deg, #10b98120, transparent, #34d39920)', borderRadius: '50%', filter: 'blur(25px)', animation: 'meshRotate 25s linear infinite', transform: 'translate(-50%, -50%)' },
  floatingOrbs: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' },
  orb1: { position: 'absolute', top: '15%', left: '15%', width: '8px', height: '8px', background: '#6366f1', borderRadius: '50%', boxShadow: '0 0 20px #6366f1', animation: 'orbFloat 6s ease-in-out infinite' },
  orb2: { position: 'absolute', top: '25%', right: '20%', width: '6px', height: '6px', background: '#ec4899', borderRadius: '50%', boxShadow: '0 0 15px #ec4899', animation: 'orbFloat 8s ease-in-out infinite 2s' },
  orb3: { position: 'absolute', bottom: '20%', left: '25%', width: '10px', height: '10px', background: '#10b981', borderRadius: '50%', boxShadow: '0 0 25px #10b981', animation: 'orbFloat 7s ease-in-out infinite 4s' },
  orb4: { position: 'absolute', bottom: '30%', right: '15%', width: '4px', height: '4px', background: '#f59e0b', borderRadius: '50%', boxShadow: '0 0 12px #f59e0b', animation: 'orbFloat 9s ease-in-out infinite 1s' },
  content: { maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 2 },
  header: { textAlign: 'center', marginBottom: '5rem' },
  titleContainer: { position: 'relative', display: 'inline-block', marginBottom: '1rem' },
  title: { fontSize: "clamp(3rem, 6vw, 5rem)", fontWeight: '800', background: 'linear-gradient(135deg, #ffffff 0%, #a1a1aa 50%, #71717a 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', letterSpacing: '-0.03em', margin: 0 },
  titleAccent: { position: 'absolute', bottom: '-10px', left: '50%', transform: 'translateX(-50%)', width: '60px', height: '4px', background: 'linear-gradient(90deg, #6366f1, #8b5cf6, #ec4899)', borderRadius: '2px' },
  subtitle: { fontSize: "clamp(1.1rem, 2vw, 1.3rem)", color: '#a1a1aa', maxWidth: '600px', margin: '0 auto', lineHeight: '1.6', fontWeight: '400' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '2rem', padding: '0 1rem' },
  card: { position: 'relative', borderRadius: '24px', border: '1px solid rgba(255, 255, 255, 0.08)', backdropFilter: 'blur(20px)', overflow: 'hidden', transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', cursor: 'pointer', background: 'rgba(255, 255, 255, 0.02)' },
  cardPattern: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, transition: 'opacity 0.4s ease' },
  cardContent: { position: 'relative', padding: '2rem', height: '100%', display: 'flex', flexDirection: 'column' },
  iconSection: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', padding: '0.5rem', borderRadius: '16px', background: 'rgba(255, 255, 255, 0.02)' },
  iconContainer: { width: '60px', height: '60px', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.4s ease' },
  icon: { fontSize: '1.8rem' },
  openingsBadge: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end' },
  openingsCount: { fontSize: '1.5rem', fontWeight: '700', lineHeight: '1' },
  openingsText: { fontSize: '0.8rem', color: '#71717a', textTransform: 'uppercase', letterSpacing: '0.05em' },
  cardBody: { flex: 1, marginBottom: '1.5rem' },
  cardTitle: { fontSize: '1.4rem', fontWeight: '600', color: '#ffffff', marginBottom: '0.75rem', lineHeight: '1.3' },
  cardDescription: { fontSize: '0.95rem', color: '#a1a1aa', lineHeight: '1.6', marginBottom: '1.5rem' },
  tagsContainer: { display: 'flex', flexWrap: 'wrap', gap: '0.5rem' },
  tag: { fontSize: '0.75rem', padding: '0.4rem 0.8rem', borderRadius: '20px', border: '1px solid', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.02em' },
  cardFooter: { marginTop: 'auto' },
  actionButton: { width: '100%', padding: '1rem 1.5rem', borderRadius: '16px', border: '1px solid', background: 'transparent', fontSize: '0.95rem', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', transition: 'all 0.3s ease', fontFamily: 'inherit' },
  buttonArrow: { fontSize: '1.1rem', transition: 'transform 0.3s ease' },
};

export default JoinOurTeams;